import { watch } from 'vue'

import { useCurrentUserStore } from '@common/current-user-layer/stores/currentUser.store'
import { useLocationStore } from '@common/location-layer/store/location.store'
import { setLocale, isLocaleSupported } from '@ghl-revex/assets-drm-client/setup'

const DEFAULT_LOCALE = 'en'

export const createLocalePreferencePlugin = () => {
  return (nuxtApp: NuxtApp) => {
    const currentUserStore = useCurrentUserStore()
    const locationStore = useLocationStore()
    const i18n = nuxtApp.$i18n as ReturnType<typeof useI18n> | undefined
    if (!i18n) return

    const supportedLocales = new Set(((i18n.localeCodes?.value ?? i18n.localeCodes) as string[]) ?? [])

    /**
     * Locale explicitly stored for this user/portal, or `null` when none exists.
     *
     * `null` deliberately does NOT mean English — it means "nobody chose a language", so
     * @nuxtjs/i18n `detectBrowserLanguage` (cookie, then `navigator.languages`) stays in charge.
     */
    const resolveStoredLocale = (): string | null => {
      const userPref = currentUserStore.profile?.languagePreference
      if (userPref && supportedLocales.has(userPref)) return userPref

      const builderPref = locationStore.languagePreferenceBuilder
      if (builderPref && supportedLocales.has(builderPref)) return builderPref

      return null
    }

    const applyDrmLocale = async () => {
      try {
        const lang = currentUserStore.profile?.languagePreference || locationStore.languagePreferenceBuilder || DEFAULT_LOCALE
        if (isLocaleSupported(lang)) {
          setLocale(lang)
        }
      } catch (error) {
        console.error('Failed to set assets drm client locale:', error)
      }
    }

    let isApplying = false
    /**
     * Set when a re-apply is requested while one is already in flight. Without it the
     * request is dropped: `i18n.setLocale` loads locale files asynchronously, so a
     * higher-priority preference arriving mid-switch (profile loading after the portal
     * builder's) — or a detection event firing mid-switch — would be discarded and
     * nothing would retry, stranding the user on the earlier locale.
     */
    let isApplyQueued = false

    const applyLocale = async () => {
      const target = resolveStoredLocale()
      void applyDrmLocale()
      if (!target) return

      if (isApplying) {
        isApplyQueued = true
        return
      }

      const current = typeof i18n.locale === 'string' ? i18n.locale : i18n.locale?.value
      if (target === current) return

      isApplying = true
      try {
        await i18n.setLocale?.(target)
      } finally {
        isApplying = false

        if (isApplyQueued) {
          isApplyQueued = false
          // Re-resolve rather than reusing `target`: the queued request exists precisely
          // because the answer may have changed while the switch was in flight.
          await applyLocale()
        }
      }
    }

    void applyLocale()

    watch(
      () => currentUserStore.profile?.languagePreference,
      () => void applyLocale()
    )

    watch(
      () => locationStore.languagePreferenceBuilder,
      () => void applyLocale()
    )

    /**
     * `@nuxtjs/i18n` registers a global `locale-changing` route middleware
     * (runtime/plugins/route-locale-detect.js) that re-runs browser/cookie detection on the
     * first navigation — i.e. AFTER this plugin has run. Without this hook that detection
     * overwrites a stored preference, so a user whose profile says English lands on the
     * browser's language instead. Re-assert only when a stored preference exists; when there
     * is none, whatever detection resolved is kept.
     */
    nuxtApp.hook('i18n:localeSwitched', ({ newLocale }: { newLocale: string }) => {
      const target = resolveStoredLocale()
      if (!target || target === newLocale) return
      void applyLocale()
    })
  }
}
